// ============================================================
// BATTLEFIELD PRESETS - maps you can drop on the table immediately.
//
// The same idea as `engine/demoScenarios.ts`: a handful of hand-built
// situations so the feature is useful before anyone has authored
// anything. Each is a plain `Terrain` record, which is exactly what the
// Firestore document holds - loading a preset and loading a saved map
// are the same operation.
//
// Pure. Zero React dependency.
// ============================================================
import type { Boundary } from './boundary';
import { makeBoundary } from './boundary';
import type { Solid } from './types';
import { makeSolid } from './solids';
import type { VoxelGrid } from './voxels';
import { meshVoxels, emptyGrid } from './voxels';
import type { Zone, SurfaceDef } from './zones';
import { meshZones } from './zones';
import { meshExcavation } from './excavation';

export interface Terrain {
  boundary: Boundary;
  /**
   * Parametric solids - the round pillars, cones and sloped hills that
   * cubes do badly, plus everything authored before v7.17.0. Kept so no
   * saved map and no preset needs migrating.
   */
  solids: Solid[];
  /** Cubes, the way maps are built now. */
  voxels?: VoxelGrid;
  /** Painted ground: hazards, rough going, the far side of a chasm. */
  zones?: Zone[];
  /**
   * The surface palette the zone tool paints from - on the terrain so a
   * map carries its own. Written by useBattlefieldBuild since v7.66.0;
   * declared here since v8.205.0 (the field predates its type).
   */
  surfaces?: Record<string, SurfaceDef>;
  /** How the scene is lit (engine/battlefield/lighting); absent is daylight. */
  lighting?: 'lit-areas' | 'darkness';
  /**
   * Terrain adjudicates nothing: the map is a diagram only.
   *
   * Session-wide and GM-set, for tables that want the picture without
   * the geometry deciding their fights. The per-combatant escape hatch
   * is `Token.ignoresTerrain`.
   */
  ignoreTerrain?: boolean;
  /**
   * A suggested deployment marker, carried so a saved map can propose
   * where a fight begins. Session state wins: this is applied only when
   * the session has no marker at all (`loadMap`).
   */
  deployment?: unknown;
  /** Shown in the library list. */
  name?: string;
}

/**
 * Everything on this map as solids, which is the only language the rest
 * of the engine speaks. Meshing the voxels is what lets line of sight,
 * movement and the renderer stay exactly as they were.
 */
export function terrainSolids(terrain: Terrain | null | undefined): Solid[] {
  if (!terrain) return [];
  const built = terrain.voxels ? meshVoxels(terrain.voxels) : [];
  const dug = meshExcavation(terrain.voxels);
  const painted = meshZones(terrain.zones, terrain.boundary?.tileSize ?? 1);
  const parametric = terrain.solids || [];
  // Concatenating only when there is something to concatenate keeps the
  // identity stable for the memoised scene in the common case.
  if (!built.length && !painted.length && !dug.length) return parametric;
  if (!parametric.length && !painted.length && !dug.length) return built;
  return [...parametric, ...built, ...dug, ...painted];
}

/** A crate: chest high, good cover, you can see over it. */
function crate(x: number, z: number, size = 1.2, height = 1.1): Solid {
  return makeSolid({
    sides: 4,
    radiusBottom: size / 2,
    radiusTop: size / 2,
    height,
    position: { x, y: 0, z },
    cover: { level: 'half', ap: 4 },
    label: 'Crate',
  });
}

/** A wall: opaque, full cover, and long. `along` is 'x' or 'z'. */
function wall(x: number, z: number, length: number, along: 'x' | 'z', height = 3): Solid {
  return makeSolid({
    sides: 4,
    radiusBottom: 0.25,
    radiusTop: 0.25,
    scaleX: along === 'x' ? length * 2 : 1,
    scaleZ: along === 'z' ? length * 2 : 1,
    height,
    position: { x, y: 0, z },
    cover: { level: 'full', ap: 8 },
    opaque: true,
    label: 'Wall',
  });
}

/** A low patch of broken ground: slow going, no cover. */
function rubble(x: number, z: number): Solid {
  return makeSolid({
    sides: 6,
    radiusBottom: 3,
    radiusTop: 3,
    height: 0.08,
    position: { x, y: 0, z },
    terrain: 'difficult',
    label: 'Rubble',
  });
}

/** A round column to hide behind entirely. */
function pillar(x: number, z: number): Solid {
  return makeSolid({
    sides: 24,
    radiusBottom: 1.2,
    radiusTop: 1.2,
    height: 4.5,
    position: { x, y: 0, z },
    cover: { level: 'full', ap: 10 },
    opaque: true,
    label: 'Pillar',
  });
}

/** A pointed mound of spoil: three-quarter cover. */
function spoilHeap(x: number, z: number): Solid {
  return makeSolid({
    sides: 4,
    radiusBottom: 1,
    radiusTop: 0,
    height: 3.5,
    position: { x, y: 0, z },
    cover: { level: 'threeQuarter', ap: 6 },
    opaque: true,
    label: 'Spoil heap',
  });
}

/**
 * The default arena: a 24 m yard with two walls, a scatter of crates and
 * a patch of rubble. Enough to make every part of the map mean something
 * - cover, an opaque sightline, slow going - without being a scenario.
 */
function courtyard(): Terrain {
  return {
    name: 'Ruined courtyard',
    boundary: makeBoundary({ sides: 4, radius: 12, tileSize: 1 }),
    solids: [
      wall(-2, -4, 6, 'x'),
      wall(5, 4, 5, 'z'),
      crate(-7, 6),
      crate(-5.5, 6.6),
      crate(8, -7),
      crate(2, 8, 1.6, 1.6),
      rubble(-8, -7),
      pillar(9, 8),
    ],
  };
}

/** A long approach with a hill in the middle - elevation matters here. */
function ridge(): Terrain {
  return {
    name: 'The ridge',
    boundary: makeBoundary({ sides: 4, radius: 10, scaleX: 2.4, tileSize: 1 }),
    solids: [
      makeSolid({
        sides: 24,
        radiusBottom: 6,
        radiusTop: 3,
        height: 3,
        position: { x: 0, y: 0, z: 0 },
        terrain: 'difficult',
        label: 'Ridge',
      }),
      crate(-14, 4),
      crate(14, -4),
      wall(-8, 7, 4, 'x', 2),
      wall(8, -7, 4, 'x', 2),
    ],
  };
}

/** An open circle with hazards - nowhere to hide, plenty to avoid. */
function pit(): Terrain {
  return {
    name: 'The pit',
    boundary: makeBoundary({ sides: 24, radius: 11, tileSize: 1 }),
    solids: [
      makeSolid({
        sides: 24,
        radiusBottom: 3,
        radiusTop: 3,
        height: 0.06,
        position: { x: 0, y: 0, z: 0 },
        terrain: 'dangerous',
        label: 'Promethium spill',
      }),
      spoilHeap(-6, 5),
      spoilHeap(6, -5),
    ],
  };
}

export const BATTLEFIELD_PRESETS = [
  { key: 'courtyard', label: 'Ruined courtyard', build: courtyard },
  { key: 'ridge', label: 'The ridge', build: ridge },
  { key: 'pit', label: 'The pit', build: pit },
] as const;

/** Build a preset by key, or null if the key is unknown. */
export function buildPreset(key: string): Terrain | null {
  return BATTLEFIELD_PRESETS.find((p) => p.key === key)?.build() ?? null;
}

/** An arena with nothing in it - what a session starts with. */
export function emptyTerrain(): Terrain {
  const boundary = makeBoundary();
  return { boundary, solids: [], voxels: emptyGrid(boundary.tileSize) };
}
